import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps, addressFromComponents } from "../lib/google_maps"
import { confirmNewCompany } from "../lib/company_duplicate"
import { track } from "../lib/mixpanel"

// Words the page didn't send (tests, an old cached page): plain English.
const FALLBACK_TEXTS = {
  find_address: "Find address", searching: "Searching…",
  matches_near: "Matches near the job · pick one", matches: "Matches · pick one",
  not_here: "Not here? Close this and type the address below.",
  no_matches: "No matches found. Type the address below.",
  lookup_failed: "Couldn't search right now. Type the address below.",
  filled_from: "Filled in from %{name}. Check it before you save.",
  from_job_mi: "%{n} mi from the job", from_job_km: "%{n} km from the job",
  type_name_first: "Type the company name first."
}

export default class extends Controller {
  static targets = [
    "name", "address", "country", "state", "city", "zipcode", "defaultLanguage",
    "nameError", "defaultLanguageError",
    "serverError", "submitBtn",
    "findBtn", "findLabel", "findResults", "findNote"
  ]

  static values = { url: String, zipLookupUrl: String, near: Array, units: String, texts: Object }

  connect() {
    // Builder personas open with Country=US preselected (Kloepfer item 9) —
    // load its states right away so the State dropdown isn't empty.
    if (this.hasCountryTarget && this.countryTarget.value) {
      this.countryChanged()
    }
  }

  disconnect() {
    if (this._zipTimer) clearTimeout(this._zipTimer)
  }

  async countryChanged() {
    const code = this.countryTarget.value
    const select = this.stateTarget
    select.innerHTML = '<option value="">Select...</option>'

    if (!code) return

    try {
      const resp = await fetch(`/locations/subregions?country_code=${code}`, {
        headers: { "Accept": "application/json" }
      })
      const subregions = await resp.json()
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.code
        opt.textContent = sr.name
        select.appendChild(opt)
      })
    } catch { /* ignore */ }
  }

  // Typing a 5-digit US zip auto-fills City + State (Kloepfer item 9; every
  // account since K8-6). Debounced; silent no-op when the lookup has no match.
  zipChanged() {
    if (!this.zipLookupUrlValue) return
    if (this._zipTimer) clearTimeout(this._zipTimer)
    this._zipTimer = setTimeout(() => this._lookupZip(), 400)
  }

  async _lookupZip() {
    const zip = this.hasZipcodeTarget ? this.zipcodeTarget.value.trim() : ""
    if (!/^\d{5}$/.test(zip)) return
    if (this.countryTarget.value && this.countryTarget.value !== "us") return

    try {
      const resp = await fetch(`${this.zipLookupUrlValue}?zip=${zip}`, {
        headers: { "Accept": "application/json" }
      })
      const data = await resp.json()
      if (!data.city) return

      if (this.hasCityTarget) this.cityTarget.value = data.city
      if (data.state_code) {
        // States may still be loading from countryChanged — retry briefly.
        this._selectState(data.state_code, 5)
      }
    } catch { /* ignore — form stays manual */ }
  }

  _selectState(code, attempts) {
    const select = this.stateTarget
    const hasOption = Array.from(select.options).some(o => o.value === code)
    if (hasOption) {
      select.value = code
    } else if (attempts > 0) {
      setTimeout(() => this._selectState(code, attempts - 1), 300)
    }
  }

  // "Find address" (Kloepfer K8-6, Larry 2026-10-08 / Phelipe 2026-10-27):
  // the typed name looked up on Google near the job; picking one of the
  // matches fills the whole address. Real listings, so nothing made up.
  async findAddress() {
    const name = this.nameTarget.value.trim()
    if (!name) {
      this.nameTarget.focus()
      this._note(this._t("type_name_first"), "muted")
      return
    }
    this._note(null)
    this._finding(true)
    let results = null
    try {
      await loadGoogleMaps(window)
      results = await this._textSearch(name)
    } catch { results = null }
    this._finding(false)
    this._showResults(results)
  }

  async pickPlace(event) {
    const index = Number(event.currentTarget.dataset.index)
    const match = this._results?.[index]
    if (!match) return
    const places = window.google.maps.places
    const place = await new Promise((resolve) => {
      this._placesService().getDetails(
        { placeId: match.place_id, fields: ["address_components", "formatted_address", "name"] },
        (p, status) => resolve(status === places.PlacesServiceStatus.OK ? p : null)
      )
    })
    this.closeResults()
    if (!place) {
      this._note(this._t("lookup_failed"), "muted")
      return
    }
    await this._fillFromPlace(place, match.name)
    track("Company Address Picked", { rank: index + 1, results: this._results.length })
  }

  closeResults() {
    if (!this.hasFindResultsTarget) return
    this.findResultsTarget.innerHTML = ""
    this.findResultsTarget.classList.add("hidden")
  }

  async submit() {
    this._clearErrors()
    if (!this._validate()) return

    this._setLoading(true)
    const name = this.nameTarget.value.trim()

    // "Already in ConsLog?" (Kloepfer K8-6, Phelipe 2026-10-27): before a
    // company is registered, ask whether it's already there under another
    // spelling. Using it hands the existing company back, like a new one.
    const { choice, company } = await confirmNewCompany(name, { screen: "register_company" })
    if (choice !== "new") {
      this._setLoading(false)
      if (choice === "existing") {
        this._resetForm()
        this.dispatch("created", { detail: { id: company.id, name: company.name, existing: true } })
        this.dispatch("close")
      }
      return
    }

    const body = {
      company: {
        name,
        address: this.hasAddressTarget ? this.addressTarget.value.trim() : "",
        country_code: this.countryTarget.value,
        sub_country_code: this.stateTarget.value,
        city: this.hasCityTarget ? this.cityTarget.value.trim() : "",
        zipcode: this.hasZipcodeTarget ? this.zipcodeTarget.value.trim() : "",
        default_language: this.defaultLanguageTarget.value
      }
    }

    try {
      const token = document.querySelector("meta[name='csrf-token']")?.content
      const resp = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify(body)
      })

      const data = await resp.json()

      if (resp.ok) {
        this._resetForm()
        this.dispatch("created", { detail: { id: data.id, name: data.name } })
        this.dispatch("close")
      } else {
        this._showServerErrors(data.errors || ["An error occurred"])
      }
    } catch {
      this._showServerErrors(["An unexpected error occurred. Please try again."])
    } finally {
      this._setLoading(false)
    }
  }

  _validate() {
    let valid = true

    if (!this.nameTarget.value.trim()) {
      this.nameTarget.classList.add("border-red-400", "ring-1", "ring-red-400")
      this.nameErrorTarget.classList.remove("hidden")
      valid = false
    }

    if (!this.defaultLanguageTarget.value) {
      this.defaultLanguageTarget.classList.add("border-red-400", "ring-1", "ring-red-400")
      this.defaultLanguageErrorTarget.classList.remove("hidden")
      valid = false
    }

    return valid
  }

  _clearErrors() {
    const fields = [this.nameTarget, this.defaultLanguageTarget]
    fields.forEach(f => f.classList.remove("border-red-400", "ring-1", "ring-red-400"))

    const errors = [this.nameErrorTarget, this.defaultLanguageErrorTarget]
    errors.forEach(e => e.classList.add("hidden"))

    this.serverErrorTarget.classList.add("hidden")
    this.serverErrorTarget.textContent = ""
  }

  _showServerErrors(errors) {
    this.serverErrorTarget.innerHTML = errors.map(e => `<div>${this._esc(e)}</div>`).join("")
    this.serverErrorTarget.classList.remove("hidden")
  }

  // Back to how the pop-up opened, starting values included: blanking Country
  // and Language here is why Larry's second company got no State (K8-6).
  _resetForm() {
    const inputs = [this.nameTarget]
    if (this.hasAddressTarget) inputs.push(this.addressTarget)
    if (this.hasCityTarget) inputs.push(this.cityTarget)
    if (this.hasZipcodeTarget) inputs.push(this.zipcodeTarget)
    inputs.forEach(i => { i.value = "" })

    this._restoreDefault(this.countryTarget)
    this.stateTarget.innerHTML = '<option value="">Select...</option>'
    if (this.countryTarget.value) this.countryChanged()
    this._restoreDefault(this.defaultLanguageTarget)
    this.closeResults()
    this._note(null)
    this._clearErrors()
  }

  _restoreDefault(select) {
    const index = Array.from(select.options).findIndex(o => o.defaultSelected)
    select.selectedIndex = index >= 0 ? index : 0
  }

  _setLoading(loading) {
    const btn = this.submitBtnTarget
    if (loading) {
      btn.dataset.originalWidth = `${btn.offsetWidth}px`
      btn.style.width = btn.dataset.originalWidth
      this._originalLabel = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = `<svg class="animate-spin h-4 w-4 mx-auto" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
    } else {
      btn.disabled = false
      btn.innerHTML = this._originalLabel || btn.innerHTML
      btn.style.width = ""
    }
  }

  // ── Find address helpers ──────────────────────────────────────────

  _placesService() {
    if (!this._places) this._places = new window.google.maps.places.PlacesService(document.createElement("div"))
    return this._places
  }

  _textSearch(name) {
    const maps = window.google.maps
    const request = { query: name }
    const near = this._near()
    if (near) {
      request.location = new maps.LatLng(near[0], near[1])
      request.radius = 80000
    }
    return new Promise((resolve) => {
      this._placesService().textSearch(request, (results, status) => {
        const s = maps.places.PlacesServiceStatus
        resolve(status === s.OK ? results : (status === s.ZERO_RESULTS ? [] : null))
      })
    })
  }

  // The job's spot: the project page sends it; in the New Project wizard it
  // comes from the Location step's fields.
  _near() {
    let [lat, lng] = this.hasNearValue ? this.nearValue : []
    if (lat == null || lng == null) {
      lat = document.querySelector('input[name="project[latitude]"]')?.value
      lng = document.querySelector('input[name="project[longitude]"]')?.value
    }
    lat = parseFloat(lat)
    lng = parseFloat(lng)
    return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0) ? [lat, lng] : null
  }

  _showResults(results) {
    const box = this.findResultsTarget
    box.innerHTML = ""
    this._results = (results || []).slice(0, 3)
    const near = this._near()

    const panel = document.createElement("div")
    panel.className = "bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
    if (this._results.length === 0) {
      panel.appendChild(this._el("p", "px-3 py-2 text-[13px] text-gray-500", this._t(results === null ? "lookup_failed" : "no_matches")))
    } else {
      panel.appendChild(this._el("div", "px-3 py-1.5 border-b border-gray-200 bg-gray-50 text-[13px] text-gray-500",
        this._t(near ? "matches_near" : "matches")))
      const list = this._el("div", "divide-y divide-gray-100")
      this._results.forEach((r, i) => {
        const row = this._el("button", "w-full text-left flex items-start gap-2 px-3 py-2 hover:bg-purple-50 cursor-pointer")
        row.type = "button"
        row.dataset.index = String(i)
        row.dataset.action = "new-company#pickPlace"
        const text = this._el("span", "min-w-0 flex-1")
        text.appendChild(this._el("span", "block text-sm font-medium text-gray-900", r.name || ""))
        text.appendChild(this._el("span", "block text-[13px] text-gray-600", r.formatted_address || ""))
        row.appendChild(text)
        const away = this._distance(near, r)
        if (away) row.appendChild(this._el("span", "text-[13px] text-gray-400 whitespace-nowrap", away))
        list.appendChild(row)
      })
      panel.appendChild(list)
      const close = this._el("button", "w-full text-left px-3 py-1.5 border-t border-gray-200 text-[13px] text-gray-500 hover:bg-gray-50 cursor-pointer", this._t("not_here"))
      close.type = "button"
      close.dataset.action = "new-company#closeResults"
      panel.appendChild(close)
    }
    box.appendChild(panel)
    box.classList.remove("hidden")
    track("Company Address Searched", { results: this._results.length, near_job: !!near, failed: results === null })
  }

  _distance(near, result) {
    const loc = result.geometry?.location
    if (!near || !loc) return ""
    const toRad = (d) => d * Math.PI / 180
    const [lat1, lng1] = near
    const lat2 = typeof loc.lat === "function" ? loc.lat() : loc.lat
    const lng2 = typeof loc.lng === "function" ? loc.lng() : loc.lng
    const a = Math.sin(toRad(lat2 - lat1) / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lng2 - lng1) / 2) ** 2
    const km = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const miles = this.unitsValue !== "km"
    const n = miles ? km / 1.609 : km
    const shown = n < 10 ? Math.round(n * 10) / 10 : Math.round(n)
    return this._t(miles ? "from_job_mi" : "from_job_km").replace("%{n}", String(shown))
  }

  async _fillFromPlace(place, label) {
    const components = place.address_components || []
    const parts = addressFromComponents(components, place.formatted_address) || {}
    const country = components.find(c => c.types.includes("country"))?.short_name?.toLowerCase()
    if (this.hasAddressTarget) this.addressTarget.value = parts.address || ""
    if (this.hasCityTarget) this.cityTarget.value = parts.city || ""
    if (this.hasZipcodeTarget) this.zipcodeTarget.value = parts.zipcode || ""
    if (country && this.countryTarget.value !== country &&
        Array.from(this.countryTarget.options).some(o => o.value === country)) {
      this.countryTarget.value = country
      await this.countryChanged()
    }
    if (parts.state) this._selectState(parts.state.toLowerCase(), 5)
    this._note(this._t("filled_from").replace("%{name}", label || place.name || ""), "ok")
  }

  _finding(on) {
    if (this.hasFindBtnTarget) this.findBtnTarget.disabled = on
    if (this.hasFindLabelTarget) this.findLabelTarget.textContent = this._t(on ? "searching" : "find_address")
  }

  _note(text, tone = "ok") {
    if (!this.hasFindNoteTarget) return
    const note = this.findNoteTarget
    note.innerHTML = ""
    if (!text) {
      note.classList.add("hidden")
      return
    }
    const css = tone === "ok"
      ? "rounded-lg px-3 py-2 text-[13px] text-green-800 bg-green-50 border border-green-200"
      : "text-[13px] text-gray-500"
    note.appendChild(this._el("p", css, text))
    note.classList.remove("hidden")
  }

  _t(key) {
    return (this.hasTextsValue && this.textsValue[key]) || FALLBACK_TEXTS[key] || key
  }

  _el(tag, className, text) {
    const el = document.createElement(tag)
    el.className = className
    if (text != null) el.textContent = text
    return el
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }
}
